import { HostListener } from '@rhtml/decorators';
import { FlexLayout } from '@rhtml/modifiers';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement, property } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { Padding } from '@shared/modifiers/padding';
import { ScrollToService } from '@shared/services/scroll/scroll-to.service';
import { ReactivumTheme } from '@shared/styles/theme';
import { lastValueFrom } from 'rxjs';
import { delay, take, tap } from 'rxjs/operators';

import { Logo } from '../navbar/logo';

/**
 * @customElement navbar-component
 */
@Component<NavbarComponent>({
  selector: 'navbar-component',
  styles: [
    css`
      ${ReactivumTheme}

      :host {
        display: block;
      }

      .navbar {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(15px);
        position: fixed;
        top: 16px;
        left: 16px;
        right: 16px;
        z-index: 10;
        border-radius: var(--radius-xl);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.2);
        transition: all 0.3s ease;
      }

      .navbar.scrolled {
        top: 0;
        left: 0;
        right: 0;
        border-radius: 0;
        box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
        border: none;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      }

      .navbar-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 24px;
        height: 68px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .logo-section {
        display: flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        padding: 8px 12px;
        border-radius: 10px;
      }

      .logo {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.3s ease;
      }

      .logo-section:hover .logo {
        transform: scale(1.05);
      }

      .company-name {
        font-size: 1.4rem;
        font-weight: 700;
        color: var(--text-primary);
        margin: 0;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
        background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        user-select: none;
      }

      .nav-links {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .nav-link {
        color: var(--text-secondary);
        text-decoration: none;
        font-weight: 500;
        font-size: 0.95rem;
        transition: all 0.2s ease;
        position: relative;
        padding: 12px 20px;
        border-radius: 10px;
        user-select: none;
        cursor: pointer;
      }

      .nav-link:hover {
        color: var(--primary-color);
        background: rgba(0, 102, 255, 0.06);
      }

      .nav-link::after {
        content: '';
        position: absolute;
        bottom: 8px;
        left: 50%;
        width: 0;
        height: 2px;
        background: var(--primary-color);
        transition: all 0.2s ease;
        transform: translateX(-50%);
        border-radius: 1px;
      }

      .nav-link:hover::after {
        width: 40%;
      }

      .cta-button {
        padding: 12px 24px;
        border-radius: 10px;
        font-size: 0.9rem;
        margin-left: 8px;
      }

      @media (max-width: 768px) {
        .nav-links {
          display: none;
        }

        .navbar-container {
          justify-content: center;
        }

        .navbar {
          top: 8px;
          left: 8px;
          right: 8px;
        }

        .navbar.scrolled {
          top: 0;
          left: 0;
          right: 0;
        }
      }
    `,
  ],
  modifiers: [...FlexLayout, Padding],
  template(this) {
    return html`
      <nav class="navbar" id="navbar">
        <div class="navbar-container">
          <div class="logo-section" @click=${() => this.onLogoClicked()}>
            <div class="logo">${Logo}</div>
            <h1 class="company-name">Reactive Solutions</h1>
          </div>

          <div class="nav-links">
            <div class="nav-link" @click=${() => this.scrollToSection('services-component')}>Services</div>
            <div class="nav-link" @click=${() => this.scrollToSection('timeline-component')}>Journey</div>
            <div class="nav-link" @click=${() => this.scrollToSection('mission-component')}>Mission</div>
            <div class="nav-link" @click=${() => this.scrollToSection('frameworks-component')}>Frameworks</div>
            <div class="nav-link" @click=${() => this.scrollToSection('showcase-component')}>Showcase</div>
            <div class="nav-link" @click=${() => this.onBlogClicked()}>Blog</div>

            <div class="btn btn-primary cta-button" @click=${() => this.scrollToSection('contact-component')}>
              Contact Us
            </div>
          </div>
        </div>
      </nav>
    `;
  },
})
export class NavbarComponent extends LitElement {
  @property({ type: Number }) counter = 0;
  private scrollThreshold = 100;

  @Inject(ScrollToService)
  private scrollToService: ScrollToService;

  @Router() router: Router;

  @HostListener('window:scroll')
  onScroll() {
    const currentScrollY = window.scrollY;
    const navbar = this.shadowRoot?.getElementById('navbar');

    // Add scrolled class when scrolling down past threshold
    if (currentScrollY > this.scrollThreshold) {
      navbar.classList.add('scrolled');
    } else {
      navbar.classList.remove('scrolled');
    }
  }

  private async scrollToSection(componentName: string) {
    if (this.router.activePath !== '/') {
      this.router.go('/');
      await lastValueFrom(
        this.router.onSnapshotChange().pipe(
          delay(10),
          take(1),
          tap(() => this.scrollToService.scrollToElement(componentName))
        )
      );
      return;
    }
    this.scrollToService.scrollToElement(componentName);
  }

  private scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  private onLogoClicked() {
    this.router.go('/');
    this.scrollToTop();
  }

  private onBlogClicked() {
    this.router.go('/blog');
    this.scrollToTop();
  }
}
